import { type ReactNode, useId } from 'react';
import { Switch as AriaSwitch, type SwitchProps as AriaSwitchProps } from 'react-aria-components';
import { composeTwRenderProps } from '../utils';

export interface SwitchProps extends Omit<AriaSwitchProps, 'children'> {
  label: string;
  /** Explains what turning it on does. Announced as the description, not as part of the name. */
  description?: ReactNode;
}

/** On/off setting that takes effect immediately. State is shown by position, not color alone. */
export function Switch({ label, description, className, ...props }: SwitchProps) {
  const descriptionId = useId();
  return (
    <div className="flex flex-col gap-0.5">
      <AriaSwitch
        {...props}
        aria-describedby={description ? descriptionId : undefined}
        className={composeTwRenderProps(
          className,
          'group flex min-h-touch cursor-default items-center gap-3 data-[disabled]:opacity-50',
        )}
      >
        <span className="flex h-6 w-11 shrink-0 items-center rounded-full border border-border-strong bg-sunken p-0.5 transition-colors duration-150 group-data-[selected]:border-accent group-data-[selected]:bg-accent group-data-[focus-visible]:outline-2 group-data-[focus-visible]:outline-offset-2 group-data-[focus-visible]:outline-border-focus">
          <span className="h-4.5 w-4.5 rounded-full bg-fg-muted transition-transform duration-150 group-data-[selected]:translate-x-5 group-data-[selected]:bg-fg-on-accent" />
        </span>
        <span className="text-sm font-medium text-fg">{label}</span>
      </AriaSwitch>
      {description ? (
        <span id={descriptionId} className="pl-14 text-sm text-fg-muted">
          {description}
        </span>
      ) : null}
    </div>
  );
}
